<script>
import {mapActions, mapState} from 'vuex';
import tripDirections from '../../../variables/tripDirections';

export default {
  name: 'ItineraryPricesPriceIntervalTable',
  props: {
    priceIntervals: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      headers: [
        { text: 'Start date', value: 'startDate' },
        { text: 'Active', value: 'active' },
        { text: 'Booking from', value: 'bookingFrom' },
        { text: 'Booking to', value: 'bookingTo' },
        { text: 'Arrival from', value: 'arrivalFrom' },
        { text: 'Arrival to', value: 'arrivalTo' },
        { text: 'Outbound days', value: 'outboundDays' },
        { text: 'Booking flow', value: 'bookingFlow' },
        { text: 'Guaranteed', value: 'guaranteed' },
        { text: 'Fixed Ex. Rate', value: 'fixedExchangeRate' },
      ],
      days: [
        { id: 0, day: 'sunday', label: this.$t('nonDynamicTransport.days.sunday') },
        { id: 1, day: 'monday', label: this.$t('nonDynamicTransport.days.monday') },
        { id: 2, day: 'tuesday', label: this.$t('nonDynamicTransport.days.tuesday') },
        { id: 3, day: 'wednesday', label: this.$t('nonDynamicTransport.days.wednesday') },
        { id: 4, day: 'thursday', label: this.$t('nonDynamicTransport.days.thursday') },
        { id: 5, day: 'friday', label: this.$t('nonDynamicTransport.days.friday') },
        { id: 6, day: 'saturday', label: this.$t('nonDynamicTransport.days.saturday') },
      ],
    };
  },
  computed: {
    ...mapState({
      selectedPriceInterval: state => state.itinerary.selectedPriceIntervalIndex,
    }),
  } ,
  methods: {
    ...mapActions({
      copyItineraryPriceSettings: 'itinerary/copyItineraryPriceSettings',
      changePriceInterval: 'itinerary/changePriceInterval',
    }),
    editPriceInterval(item, index) {
      this.$emit('edit', { index, item });
    },
    copyPriceInterval(priceIntervalId) {
      const payload = {
        itineraryId: this.$route.params.id,
        domainId: this.$route.params.domainId,
        priceIntervalId,
      };
      this.copyItineraryPriceSettings(payload);
    },
    deletePriceInterval(interval) {
      this.$emit('delete', interval);
    },
    setSelectedPriceInterval(priceIntervalId) {
      this.changePriceInterval(priceIntervalId);
    },
    displayOutboundDays(interval) {
      const outboundDays = interval.filter(day => day.direction === tripDirections.OUTBOUND);
      const days = this.filterDays(outboundDays);
      return days.join(' | ');
    },
    filterDays(inboundDays) {
      const days = [];
      inboundDays.forEach(inboundDay => {
        this.days.filter(day => {
          if (inboundDay.day === day.id) {
            days.push(day);
          }
          return null;
        });
      });
      const sortedDays = days.sort((a, b) => a.id - b.id);
      return sortedDays.map(sortedDay => sortedDay.label);
    },
    changeStatus(index) {
      this.$emit('update-active-status', index);
    },
  },
};
</script>

<template>
  <v-data-table
    v-if="priceIntervals.length"
    :headers="headers"
    :items="priceIntervals"
    hide-actions
    disable-initial-sort
  >
    <template v-slot:items="props">
      <tr
        :class="props.item.id === selectedPriceInterval ? 'selected-price-interval' : ''"
        @click="setSelectedPriceInterval(props.item.id)"
      >
        <td class="text-xs-left">
          {{ props.item.startDate | moment('DD-MM-YYYY') }}
        </td>
        <td class="text-xs-left action">
          <div v-if="props.item.active" class="active" @click="changeStatus(props.item.id)">Active</div>
          <div v-else class="deactivated" @click="changeStatus(props.item.id)">Deactivated</div>
        </td>
        <td class="text-xs-left">
          {{ props.item.bookFrom | moment('DD-MM-YYYY') }}
        </td>
        <td class="text-xs-left">
          {{ props.item.bookTo | moment('DD-MM-YYYY') }}
        </td>
        <td class="text-xs-left">
          {{ props.item.arrivalFrom | moment('DD-MM-YYYY') }}
        </td>
        <td class="text-xs-left">
          {{ props.item.arrivalTo | moment('DD-MM-YYYY') }}
        </td>
        <td class="text-xs-left">
          {{ displayOutboundDays(props.item.intervalDays) }}
        </td>
        <td class="text-xs-left">
          {{ props.item.bookingFlow ? 'Yes' : 'No' }}
        </td>
        <td class="text-xs-left">
          {{ props.item.guaranteed ? 'Yes' : 'No' }}
        </td>
        <td class="text-xs-left">
          {{ props.item.fixedExchangeRate ? 'Yes' : 'No' }}
        </td>
        <td class="text-xs-right">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                fab
                small
                v-on="on"
                @click="editPriceInterval(props.item, props.index)"
              >
                <v-icon>edit</v-icon>
              </v-btn>
            </template>
            <span>Edit price interval</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                :disabled="!props.item.id"
                color="primary"
                fab
                small
                v-on="on"
                @click="copyPriceInterval(props.item.id)"
              >
                <v-icon>copy_all</v-icon>
              </v-btn>
            </template>
            <span>Copy price interval</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="error"
                fab
                small
                v-on="on"
                @click="deletePriceInterval(props.item)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
            <span>Delete price interval</span>
          </v-tooltip>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<style scoped lang="scss">
  @import '../../../assets/scss/variables';

  .selected-price-interval {
    background-color: $highlight-color-lightgrey;
  }

  .action {
    cursor: pointer;
  }

  .active {
    color: $success-color;
  }

  .deactivated {
    color: $warning-color;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesPriceIntervalTable.vue